/**
 * خطِ لوله‌ی منبعِ کرالر — خواندن، ویرایش، افزودن (مایگریشن 0076).
 *
 * قرینه‌ی `opsRepo` و به همان دلیل جدا از آن: `opsRepo` داشبوردِ **شمارش**
 * است (سنکی، فازها، سلامت) و این‌جا **ردیف‌ها**ست. یکی کردنشان یعنی یک
 * فایل که هر تغییری در هر سربرگ به آن دست می‌زند.
 *
 * ═══ چرا نوشتن این‌جا «درخواست» است و نه نوشتن ═══
 *
 * `crawler.source_versions` سه نامتغیر دارد که در پایتون زندگی می‌کنند —
 * `behavior_hash` (روی سریال‌سازیِ دقیقِ `json.dumps` پایتون)، ادعای
 * `(city, domain)`، و «نسخه فقط وقتی رفتار عوض شده». پس ویرایش این‌جا در
 * `public.crawler_jobs` ثبت می‌شود و کرالر با `registry.put()` اعمالش
 * می‌کند. توضیحِ کاملش بالای مایگریشن 0076 است.
 *
 * تنها استثنا `addDraft` است: `crawler.source_drafts` هیچ‌کدام از آن سه را
 * ندارد، پس ردیفِ دستی همان لحظه ثبت می‌شود و `crawl promote` بدونِ هیچ
 * تغییری برش می‌دارد.
 */
import { supabase } from '../lib/supabase';
import type { PipelineRow } from '../lib/sourcePipeline';
import type { SourceTestPage, SourceTestRun } from '../lib/sourceTest';
import type { SourceTodo, TodoKind } from '../lib/sourcePipeline';

/** یک شهر در `crawler.cities` — دامنه‌ی کشفِ خودکار. */
export interface PipelineCity {
  city: string;
  label: string;
  timezone: string;
  enabled: boolean;
  drafts: number;
  sources: number;
  last_discovery: string | null;
}

export interface CrawlerJob {
  id: number;
  kind: 'discover' | 'inspect' | 'promote' | 'edit_source' | 'add_source';
  target: string | null;
  status: 'queued' | 'running' | 'done' | 'failed';
  payload: Record<string, unknown>;
  result: Record<string, unknown> | null;
  error: string | null;
  requested_at: string;
  started_at: string | null;
  finished_at: string | null;
}

/** یک حکمِ غربالگری، به‌ازای یک آدرس. */
export interface InspectionRow {
  source_id: string;
  url: string;
  is_draft: boolean;
  verdict: string;
  listing_verdict: string;
  outcome: string;
  looks_usable: boolean;
  has_events: boolean;
  probe_url: string | null;
  probe_verdict: string | null;
  notes: string[];
  inspector_version: number;
  inspected_at: string;
}

/** یک نسخه‌ی رجیستری. `active` یعنی همینی که امروز اجرا می‌شود. */
export interface SourceVersionRow {
  source_id: string;
  version: number;
  active: boolean;
  behavior_hash: string;
  payload: Record<string, unknown>;
  rationale: string | null;
  created_at: string;
  retired_at: string | null;
}

/** یک اجرای تست با گزارشِ کاملش. `report` را `lib/sourceTest` می‌خواند. */
export interface SourceTestRunDetail {
  run: SourceTestRun & { report: unknown };
  pages: SourceTestPage[];
}

/** بدنه‌ی یک صفحه‌ی گرفته‌شده — تنها چیزی که به‌درخواست خوانده می‌شود. */
export interface SourceTestPageBody extends SourceTestPage {
  test_id: number;
  body: string | null;
}

export interface SourceDetail {
  city: string;
  bare: string;
  draft: {
    draft_id: string; city: string; label: string; domain: string;
    urls: string[]; payload: Record<string, unknown>;
    promote_version: number | null; promote_outcome: string | null;
    promoted_at: string | null; created_at: string; discovery_run_id: number | null;
  } | null;
  discovery: {
    id: number; city: string; template_version: number;
    model: string | null; candidates: number; ran_at: string;
  } | null;
  inspections: InspectionRow[];
  versions: SourceVersionRow[];
  domains: { city: string; domain: string }[];
  documents: {
    url: string; kind: string | null; http_status: number; last_fetched_at: string;
  }[];
  jobs: CrawlerJob[];
}

const call = async <T>(fn: string, args: Record<string, unknown>): Promise<T> => {
  const { data, error } = await supabase.rpc(fn, args);
  if (error) throw error;
  return data as T;
};

export const sourceRepo = {
  /** شهرهای ثبت‌شده — شاملِ شهری که هنوز یک صفحه هم برایش گرفته نشده. */
  cities: () => call<PipelineCity[]>('admin_source_cities', {}),

  /**
   * ردیف‌های خطِ لوله.
   *
   * ⚠️ فیلترِ **مرحله** این‌جا نیست و عمداً هم نیست: مرحله در
   * `lib/sourcePipeline.stageOf` تعریف می‌شود و اگر SQL هم تعریفش کند،
   * روزی جدول و فیلترش دو چیز می‌گویند. سیصد ردیف در مرورگر فیلتر شدنی
   * است؛ دو تعریفِ موازی نه.
   */
  pipeline: (city?: string | null, q?: string | null, limit = 300) =>
    call<PipelineRow[]>('admin_source_pipeline', {
      p_city: city || null, p_q: q || null, p_limit: limit,
    }),

  /** `p_bare` شناسه‌ی بدونِ فضای‌نام است (`nac`)، نه `draft:ottawa:nac`. */
  detail: (city: string, bare: string) =>
    call<SourceDetail>('admin_source_detail', { p_city: city, p_bare: bare }),

  jobs: (limit = 40) => call<CrawlerJob[]>('admin_source_jobs', { p_limit: limit }),

  /** فقط کارِ هنوز-برداشته‌نشده. کارِ در حالِ اجرا از این‌جا متوقف نمی‌شود. */
  cancelJob: (id: number) => call<boolean>('admin_source_job_cancel', { p_id: id }),

  /**
   * ثبتِ کار برای کرالر.
   *
   * تکراری خطا نیست: کارِ در صف با همان (نوع، هدف) به‌روز می‌شود و همان
   * ردیف برمی‌گردد. دو بار زدنِ «بررسی کن» نباید دو بار به سایتِ طرف
   * درخواست بزند.
   */
  queue: (
    kind: CrawlerJob['kind'],
    target?: string | null,
    payload: Record<string, unknown> = {},
  ) => call<CrawlerJob>('admin_source_job', {
    p_kind: kind, p_target: target ?? null, p_payload: payload,
  }),

  /**
   * «همین حالا امتحانش کن» — شش قدمِ اولِ خطِ لوله، بی‌آنکه چیزی بنویسد.
   *
   * ⚠️ `settings` تنظیماتِ **هنوز-ثبت‌نشده**ی فرمِ ویرایش است. با دادنش،
   * کرالر یک منبعِ موقت در حافظه می‌سازد و هیچ نسخه‌ای در رجیستری ثبت
   * نمی‌شود — یعنی «اول ببین جواب می‌دهد، بعد ثبت کن» ممکن می‌شود، که
   * تا امروز نبود و هر آزمایش یک نسخه‌ی رجیستری هزینه داشت.
   *
   * نتیجه فوری نیست: کار در همان صفِ `crawler_jobs` می‌نشیند و
   * `crawl jobs --watch` برش می‌دارد. ردیفِ برگشتی `queued` است و رابط
   * باید دنبالش کند.
   */
  test: (target: string, settings?: Record<string, unknown> | null) =>
    call<SourceTestRun>('admin_source_test', {
      p_target: target, p_settings: settings ?? null,
    }),

  /** اجراهای اخیرِ یک منبع، **بدونِ** گزارش — این همان چیزی است که polling می‌خواند. */
  testRuns: (target: string, limit = 5) =>
    call<SourceTestRun[]>('admin_source_test_runs', { p_target: target, p_limit: limit }),

  testRun: (id: number) =>
    call<SourceTestRunDetail>('admin_source_test_run', { p_id: id }),

  /** بدنه‌ی یک صفحه. جدا، چون چهار صفحه به‌راحتی یک مگابایت می‌شود. */
  testPage: (id: number) =>
    call<SourceTestPageBody>('admin_source_test_page', { p_id: id }),

  testDelete: (id: number) => call<boolean>('admin_source_test_delete', { p_id: id }),

  /**
   * ویرایشِ یک پیش‌نویس — **فوری**، بی صف.
   *
   * ⚠️ برخلافِ `queue('edit_source', …)` این یکی همان لحظه می‌نشیند، و
   * دلیلش همان دلیلِ `addDraft` است: `source_drafts` هیچ‌کدام از سه
   * نامتغیرِ `source_versions` را ندارد، پس نوشتنش از SQL بی‌خطر است.
   *
   * ⚠️ حکمِ ترفیعِ ردیف پاک می‌شود — ویرایشِ آدرس یعنی سؤال عوض شده و
   * جوابِ قبلی دیگر معتبر نیست. بدونش، پیش‌نویسِ «ردشده»ای که آدرسش را
   * درست کرده‌ای تا ابد ردشده می‌ماند.
   */
  editDraft: (draftId: string, patch: Record<string, unknown>) =>
    call<{ draft_id: string; label: string; urls: string[] }>(
      'admin_source_edit_draft', { p_draft_id: draftId, p_patch: patch },
    ),

  /**
   * حکمِ دستی روی یک پیش‌نویس — «خودم می‌گویم این یکی نه».
   *
   * ⚠️ `reason` که خالی باشد، ردیف به صف برمی‌گردد («پیش‌نویس»). متن که
   * بدهی، «ردشده» می‌شود و همان متن زیرِ ردیف دیده می‌شود.
   *
   * ⚠️ روی منبعی که ردیفِ رجیستری دارد کار نمی‌کند و خطا می‌دهد: آن‌جا
   * رجیستری بر همه‌چیز مقدم است و «ردشده» هیچ اثری در جدول نمی‌گذاشت.
   * راهِ متوقف کردنِ منبعِ فعال `enabled: false` است.
   */
  setDraftVerdict: (draftId: string, reason: string | null) =>
    call<{ draft_id: string; promote_outcome: string | null }>(
      'admin_source_set_draft_verdict', { p_draft_id: draftId, p_reason: reason },
    ),

  /**
   * فرستادنِ یک منبع به صفِ «کارِ استخراجگر» — تصمیمِ آدم، نه حدسِ ابزار.
   *
   * ⚠️ `testId` که بدهی، وضعیتِ همان لحظه (خواننده، چند آیتم، چند فیلد)
   * کپی می‌شود در ردیف. ارجاع کافی نیست: فقط پنج اجرای آخرِ هر منبع
   * می‌ماند، پس شش ماه بعد «چرا این‌جاست؟» جوابی نداشت.
   *
   * دو بار فرستادن خطا نیست — همان ردیف به‌روز می‌شود.
   */
  todo: (target: string, kind: TodoKind, note?: string | null, testId?: number | null) =>
    call<SourceTodo>('admin_source_todo', {
      p_target: target, p_kind: kind,
      p_note: note ?? null, p_test_id: testId ?? null,
    }),

  /** «کد نوشته شد» — یا با `reopen` برگرداندن به صف. */
  todoClose: (id: number, note?: string | null, reopen = false) =>
    call<boolean>('admin_source_todo_close', {
      p_id: id, p_note: note ?? null, p_open: reopen,
    }),

  /** تنها نوشتنِ فوری. `name` باید `^[a-z0-9_]+$` باشد. */
  addDraft: (input: {
    city: string; name: string; label: string; urls: string[]; note?: string | null;
  }) => call<{ draft_id: string; city: string; bare: string }>('admin_source_add_draft', {
    p_city: input.city, p_name: input.name, p_label: input.label,
    p_urls: input.urls, p_note: input.note ?? null,
  }),
};
